import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Briefcase, Handshake, HardHat, Loader2, UserRound } from "lucide-react";
import { TeaLogo } from "@/components/TeaLogo";
import { TeaWordmark } from "@/components/TeaWordmark";
import { goldBtn, inputCls } from "@/pages/partner/PartnerLoginPage";
import { useResidentHomeScreen } from "@/components/portal/AddToHomeScreen";

export const RESIDENT_TOKEN_KEY = "tea_resident_token";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHOICES = [
  ["staff", "Staff", "Admin OS for the TEA team", Briefcase, "/admin/login"],
  ["resident", "Resident", "Your tasks, schedule, memos and documents", UserRound, null],
  ["partner", "Partner", "Churches, employers and program partners", Handshake, "/partner/login"],
  ["vendor", "Contract Vendor", "Employers who hired TEA residents: hours, invoices, memos", HardHat, "/vendor/login"],
];

function ResidentForm() {
  const nav = useNavigate();
  const [phone, setPhone] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const { data } = await axios.post(`${API}/lp/auth/login-pin`, { identifier: phone, pin });
      if (data.kind !== "resident") { setError("That's a staff account — choose Staff to sign in."); return; }
      localStorage.setItem(RESIDENT_TOKEN_KEY, data.token);
      if (data.desk_token) { localStorage.setItem("nex_token", data.desk_token); window.location.href = "/admin"; return; }
      nav("/portal/resident");
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="resident-login-form">
      <input type="tel" inputMode="tel" autoComplete="tel" required placeholder="Mobile number" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="resident-login-phone" className={inputCls} />
      <input type="password" inputMode="numeric" autoComplete="current-password" required placeholder="6-digit PIN" value={pin}
        onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))} data-testid="resident-login-pin" className={`${inputCls} tracking-[0.4em] font-mono`} />
      {error && <p data-testid="resident-login-error" className="text-sm text-red-300">{error}</p>}
      <button type="submit" disabled={busy || pin.length !== 6} data-testid="resident-login-submit" className={goldBtn}>{busy && <Loader2 className="h-4 w-4 animate-spin" />} Sign in</button>
      <p className="text-sm text-white/50">Don't have a PIN yet? Your case manager sets it for you.</p>
    </form>
  );
}

function ForgotForm() {
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  const [error, setError] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try { const { data } = await axios.post(`${API}/lp/auth/forgot-pin`, { identifier: phone }); setSent(data.message); }
    catch (err) { setError(err?.response?.data?.detail || "Something went wrong. Please try again."); } finally { setBusy(false); }
  };
  if (sent) return <p data-testid="forgot-pin-sent" className="rounded-md border border-emerald-400/30 bg-emerald-400/[0.06] p-4 text-sm text-emerald-100">{sent}</p>;
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="forgot-pin-form">
      <p className="text-sm text-white/60">Enter your mobile number. We'll ask your case manager to set a new PIN for you.</p>
      <input type="tel" inputMode="tel" autoComplete="tel" required placeholder="Mobile number" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="forgot-pin-phone" className={inputCls} />
      {error && <p data-testid="forgot-pin-error" className="text-sm text-red-300">{error}</p>}
      <button type="submit" disabled={busy} data-testid="forgot-pin-submit" className={goldBtn}>{busy && <Loader2 className="h-4 w-4 animate-spin" />} Ask for a new PIN</button>
    </form>
  );
}

/** Website "Portal Login": choose Staff / Resident / Partner. Residents sign in right here with mobile number + PIN. */
export default function PortalLoginPage() {
  const [sp, setSp] = useSearchParams();
  const as = sp.get("as");
  useResidentHomeScreen();
  return (
    <div data-testid="portal-login-page" className="min-h-screen bg-[#07080a] text-white flex items-center justify-center px-5 py-16">
      <div className="w-full max-w-md space-y-8">
        <div className="space-y-2">
          <Link to="/" aria-label="Home"><TeaLogo size={72} testid="portal-login-logo" className="mb-3" /></Link>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#d4a017]"><TeaWordmark small text="The Exchange Alliance" testid="portal-login-wordmark" /> · Portal Login</div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">{as === "forgot" ? "Forgot your PIN?" : as === "resident" ? "Resident sign in" : "Who's signing in?"}</h1>
        </div>
        {as === "forgot" ? (
          <>
            <ForgotForm />
            <button type="button" onClick={() => setSp({ as: "resident" })} data-testid="forgot-pin-back" className="text-sm text-white/50 hover:text-white">← Back to sign in</button>
          </>
        ) : as === "resident" ? (
          <>
            <ResidentForm />
            <div className="flex items-center justify-between gap-3">
              <button type="button" onClick={() => setSp({})} data-testid="portal-login-back" className="text-sm text-white/50 hover:text-white">← Choose a different portal</button>
              <button type="button" onClick={() => setSp({ as: "forgot" })} data-testid="resident-login-forgot" className="text-sm text-[#d4a017] hover:underline">Forgot PIN?</button>
            </div>
          </>
        ) : (
          <div className="space-y-3">
            {CHOICES.map(([k, label, sub, Icon, href]) => {
              const cls = "w-full flex items-center gap-4 rounded-md border border-white/10 bg-white/[0.03] px-5 py-4 text-left hover:border-[#d4a017]/60 hover:bg-[#d4a017]/[0.06] transition-colors";
              const body = (<><Icon className="h-6 w-6 text-[#d4a017] shrink-0" /><span><span className="block font-semibold">{label}</span><span className="block text-sm text-white/50">{sub}</span></span></>);
              return href
                ? <Link key={k} to={href} data-testid={`portal-login-choice-${k}`} className={cls}>{body}</Link>
                : <button key={k} type="button" onClick={() => setSp({ as: k })} data-testid={`portal-login-choice-${k}`} className={cls}>{body}</button>;
            })}
          </div>
        )}
      </div>
    </div>
  );
}
